/** Seed threads + WhatsApp template bodies for the in-app WhatsApp mock lab. */

export type MockMessage = {
  id: string;
  direction: 'in' | 'out' | 'system';
  text: string;
  at: string;
  eventKey?: string;
};

export type MockThread = {
  id: string;
  kolName: string;
  phone: string;
  campaignName: string;
  agencyName: string;
  brandName: string;
  invitationStatus: string | null;
  scriptStatus: string | null;
  submissionStatus: string | null;
  paymentStatus: string | null;
  scriptPipelinePhase: string | null;
  invitationUrl: string;
  dashboardUrl: string;
  comment: string;
  lastPreview: string;
  messages: MockMessage[];
};

export const WA_TEMPLATE_BODIES: Record<string, string> = {
  INVITATION_SENT:
    'Hi {{kolName}}! New invite: {{campaignName}} ({{agencyName}}). Open: {{invitationUrl}}',
  BROADCAST_SENT:
    'Heads up from {{agencyName}}: broadcast went out for {{campaignName}} ({{count}} invites).',
  INVITATION_RESPONDED: 'Update: {{kolName}} {{action}} for {{campaignName}}.',
  SCRIPT_SUBMITTED: 'Script for {{campaignName}} is ready for your review in myKOL.',
  SCRIPT_APPROVED:
    'Good news: script for {{campaignName}} was approved. Check myKOL for next steps.',
  CONTENT_SUBMITTED:
    '{{kolName}} submitted content for {{campaignName}} — please review in myKOL.',
  CONTENT_REVISED: 'Revision requested on {{campaignName}}. Comment: {{comment}}',
  CONTENT_APPROVED: 'Your content for {{campaignName}} was approved. Nice work!',
  JOB_CREATED: 'Campaign {{campaignName}} was created in myKOL.',
  JOB_OPENED: 'Campaign {{campaignName}} is now open.',
  SHORTLIST_READY: 'Shortlist update for {{campaignName}} in myKOL.',
  PAYMENT_REMINDER_MANUAL:
    'Hi {{kolName}}, friendly reminder: payment for {{campaignName}} is still pending. Details in myKOL: {{dashboardUrl}}',
  FOLLOW_UP_PENDING:
    'Hi {{kolName}}, just checking in on the {{campaignName}} invite — can you accept or reply in myKOL? {{invitationUrl}}',
  FOLLOW_UP_SCRIPT_REVISION:
    'Hi {{kolName}}, gentle reminder to update the script for {{campaignName}}. Notes: {{comment}} — {{dashboardUrl}}',
  FOLLOW_UP_SUBMISSION_REVISION:
    'Hi {{kolName}}, please re-upload the revised video for {{campaignName}}. Feedback: {{comment}} — {{dashboardUrl}}',
  MODIFICATION_REPLY:
    'Hi {{kolName}}, we reviewed your modification request for {{campaignName}}. Please check myKOL for our response.',
};

export const WHATSAPP_MOCK_SEED_THREADS: MockThread[] = [
  {
    id: 't1',
    kolName: 'Sarah Chen',
    phone: '+60111222333',
    campaignName: 'Nike Run Club MY',
    agencyName: 'myKOL Agency',
    brandName: 'Nike',
    invitationStatus: 'PENDING',
    scriptStatus: null,
    submissionStatus: null,
    paymentStatus: null,
    scriptPipelinePhase: 'V1_BRIEF',
    invitationUrl: 'https://dev.kol.my/invite/nike-run-demo',
    dashboardUrl: 'https://dev.kol.my/kol/dashboard',
    comment: '',
    lastPreview: 'Waiting for invite response…',
    messages: [
      {
        id: 'm1',
        direction: 'out',
        text: 'Hi Sarah! New invite: Nike Run Club MY (myKOL Agency). Open: https://dev.kol.my/invite/nike-run-demo',
        at: '2026-07-10T09:12:00+08:00',
        eventKey: 'INVITATION_SENT',
      },
      {
        id: 'm2',
        direction: 'system',
        text: 'Outbox · WHATSAPP · INVITATION_SENT · SENT',
        at: '2026-07-10T09:12:02+08:00',
      },
    ],
  },
  {
    id: 't2',
    kolName: 'Ahmad Fariz',
    phone: '+60139876543',
    campaignName: 'Samsung Galaxy Creator',
    agencyName: 'myKOL Agency',
    brandName: 'Samsung',
    invitationStatus: 'ACCEPTED',
    scriptStatus: 'REVISION_REQUESTED',
    submissionStatus: null,
    paymentStatus: null,
    scriptPipelinePhase: 'V4_BRAND_REVIEW',
    invitationUrl: 'https://dev.kol.my/kol/invitations/inv_af',
    dashboardUrl: 'https://dev.kol.my/kol/dashboard',
    comment: 'Please shorten the CTA and mention Galaxy AI once.',
    lastPreview: 'Revision requested on script',
    messages: [
      {
        id: 'm1',
        direction: 'out',
        text: 'Hi Ahmad! Welcome aboard Samsung Galaxy Creator.',
        at: '2026-07-08T11:00:00+08:00',
        eventKey: 'INVITATION_RESPONDED',
      },
      {
        id: 'm2',
        direction: 'in',
        text: 'Script draft submitted yesterday 👍',
        at: '2026-07-09T16:40:00+08:00',
      },
      {
        id: 'm3',
        direction: 'out',
        text: 'Revision requested on Samsung Galaxy Creator. Comment: Please shorten the CTA and mention Galaxy AI once.',
        at: '2026-07-11T10:05:00+08:00',
        eventKey: 'CONTENT_REVISED',
      },
    ],
  },
  {
    id: 't3',
    kolName: 'Priya Kumar',
    phone: '+60165551234',
    campaignName: 'Shopee Beauty Week',
    agencyName: 'myKOL Agency',
    brandName: 'Shopee',
    invitationStatus: 'ACCEPTED',
    scriptStatus: 'APPROVED_BY_BRAND',
    submissionStatus: 'REVISION_REQUESTED',
    paymentStatus: null,
    scriptPipelinePhase: 'V6_AGENCY_QA',
    invitationUrl: 'https://dev.kol.my/kol/invitations/inv_pk',
    dashboardUrl: 'https://dev.kol.my/kol/dashboard',
    comment: 'Fix audio levels in the first 5 seconds.',
    lastPreview: 'Video revision pending',
    messages: [
      {
        id: 'm1',
        direction: 'out',
        text: 'Good news: script for Shopee Beauty Week was approved. Check myKOL for next steps.',
        at: '2026-07-07T14:20:00+08:00',
        eventKey: 'SCRIPT_APPROVED',
      },
      {
        id: 'm2',
        direction: 'in',
        text: 'Uploaded first cut to Drive.',
        at: '2026-07-09T19:10:00+08:00',
      },
      {
        id: 'm3',
        direction: 'out',
        text: 'Revision requested on Shopee Beauty Week. Comment: Fix audio levels in the first 5 seconds.',
        at: '2026-07-11T08:30:00+08:00',
        eventKey: 'CONTENT_REVISED',
      },
    ],
  },
  {
    id: 't4',
    kolName: 'Wei Ling Tan',
    phone: '+60172229876',
    campaignName: 'GrabFood Ramadan',
    agencyName: 'myKOL Agency',
    brandName: 'Grab',
    invitationStatus: 'ACCEPTED',
    scriptStatus: 'APPROVED_BY_BRAND',
    submissionStatus: 'APPROVED',
    paymentStatus: 'PENDING',
    scriptPipelinePhase: 'V7_BRAND_FINAL',
    invitationUrl: 'https://dev.kol.my/kol/invitations/inv_wl',
    dashboardUrl: 'https://dev.kol.my/kol/dashboard',
    comment: '',
    lastPreview: 'Payment pending — no MessagingEventKey yet',
    messages: [
      {
        id: 'm1',
        direction: 'out',
        text: 'Your content for GrabFood Ramadan was approved. Nice work!',
        at: '2026-07-06T12:00:00+08:00',
        eventKey: 'CONTENT_APPROVED',
      },
      {
        id: 'm2',
        direction: 'system',
        text: 'Payment milestone PENDING — WhatsApp event key not wired in production yet (see API docs gap).',
        at: '2026-07-11T09:00:00+08:00',
      },
    ],
  },
  {
    id: 't5',
    kolName: 'Daniel Ong',
    phone: '+60183334455',
    campaignName: 'Lazada 7.7 Live',
    agencyName: 'myKOL Agency',
    brandName: 'Lazada',
    invitationStatus: 'MODIFICATION_REQUESTED',
    scriptStatus: null,
    submissionStatus: null,
    paymentStatus: null,
    scriptPipelinePhase: 'V1_BRIEF',
    invitationUrl: 'https://dev.kol.my/kol/invitations/inv_do',
    dashboardUrl: 'https://dev.kol.my/kol/dashboard',
    comment: 'Can we bump rate to RM 2,500?',
    lastPreview: 'Modification requested',
    messages: [
      {
        id: 'm1',
        direction: 'out',
        text: 'Hi Daniel! New invite: Lazada 7.7 Live (myKOL Agency).',
        at: '2026-07-09T10:00:00+08:00',
        eventKey: 'INVITATION_SENT',
      },
      {
        id: 'm2',
        direction: 'in',
        text: 'Happy to join if we can bump the rate to RM 2,500.',
        at: '2026-07-09T15:22:00+08:00',
      },
    ],
  },
];

const STORAGE_KEY = 'mykol-wa-mock-threads-v2';

export function loadWhatsappMockThreads(): MockThread[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as MockThread[];
      if (Array.isArray(parsed) && parsed.length) return parsed;
    }
  } catch {
    /* ignore */
  }
  const seed = structuredClone(WHATSAPP_MOCK_SEED_THREADS);
  saveWhatsappMockThreads(seed);
  return seed;
}

export function saveWhatsappMockThreads(threads: MockThread[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(threads));
  } catch {
    /* ignore */
  }
}

export function resetWhatsappMockThreads(): MockThread[] {
  localStorage.removeItem(STORAGE_KEY);
  return loadWhatsappMockThreads();
}
